import React, { useEffect, useState } from "react";
import { Button, Drawer, Skeleton, Tag, Typography } from "antd";
import { EditOutlined, PlusOutlined } from "@ant-design/icons";
import { useLazyQuery } from "@apollo/client";
import { useDevice } from "../../../hooks/useDevice";
import { GET_MESSAGE_FEEDBACK_LIST } from "../../../graphql/query";
import { convertUtcToLocal } from "../../../library/utils";

// Backend timestamps are naive UTC; render them in the viewer's local timezone.
const FEEDBACK_DATE_FORMAT = "DD MMM, YYYY, hh:mm a";

// Shared look for the topic (feedback reason) tags — per design.
const TOPIC_TAG_STYLE = { borderRadius: 4, border: "1px solid #F0F0F0", background: "#EEE" };

// Left-border accent per feedback rating.
const ratingColor = rating => {
  const value = (rating || "").toUpperCase();
  if (value === "GOOD") return "#52C41A";
  if (value === "BAD") return "#FF4D4F";
  return "#1677FF";
};

const isSameUser = (a, b) => a != null && b != null && String(a) === String(b);

const MessageFeedbackList = ({
  isDrawerOpen,
  onClose,
  onAddNew,
  onEdit,
  currentUserId,
  messageId,
}) => {
  const { isMobile } = useDevice();
  const [comments, setComments] = useState([]);

  // Fetch every feedback left on this message, across all users (not user-scoped).
  const [getFeedbacks, { loading }] = useLazyQuery(GET_MESSAGE_FEEDBACK_LIST, {
    fetchPolicy: "network-only",
    onCompleted: data => {
      const list = data?.getMessageFeedbacks || [];
      setComments(
        list.map(item => ({
          id: item.id,
          authorId: item.userId,
          authorName: item.user?.fullName,
          authorRole: item.user?.companyRole?.name,
          authorLocation: item.user?.location?.title,
          rating: item.rating,
          comment: item.additionalComments,
          status: item.status,
          // The "unhelpful" reasons the user selected, shown as tags on the card.
          topics: (item.messageFeedbackOptionSelection || [])
            .map(selection => selection.messageFeedbackOption?.name)
            .filter(Boolean),
          // Who resolved this and when (updatedBy/updateTs are the resolution
          // audit fields — there is no dedicated resolvedBy column).
          resolvedBy: item.updatedBy?.fullName || "",
          resolvedTime: item.updateTs ? convertUtcToLocal(item.updateTs, FEEDBACK_DATE_FORMAT) : "",
          date: item.createTs ? convertUtcToLocal(item.createTs, FEEDBACK_DATE_FORMAT) : "",
        }))
      );
    },
    onError: error => console.error("Error fetching message feedback list:", error),
  });

  useEffect(() => {
    if (isDrawerOpen && messageId) {
      getFeedbacks({ variables: { messageId } });
    } else if (!isDrawerOpen) {
      setComments([]);
    }
  }, [isDrawerOpen, messageId, getFeedbacks]);

  // A user can have only one feedback per message. Hide "Add New Feedback" once
  // the logged-in user already has one so they don't expect to add another (they edit).
  const hasOwnFeedback = comments.some(comment => isSameUser(comment.authorId, currentUserId));

  return (
    <Drawer
      destroyOnClose
      title="Message Feedback"
      placement="right"
      width={isMobile ? "100%" : 600}
      onClose={() => onClose?.(true)}
      open={isDrawerOpen}
    >
      <Typography.Text className="block mb-3 text-xs font-semibold uppercase text-gray-400">
        Feedback added by:
      </Typography.Text>

      {loading ? (
        <div className="flex flex-col gap-3">
          {[1, 2].map(key => (
            <div key={key} className="rounded-md bg-gray-50 p-3">
              <Skeleton active paragraph={{ rows: 2 }} />
            </div>
          ))}
        </div>
      ) : (
        <div className="flex flex-col gap-3">
          {comments.map(comment => {
            const isOwn = isSameUser(comment.authorId, currentUserId);
            const isResolved = (comment.status || "").toUpperCase() === "RESOLVED";
            return (
              <div
                key={comment.id}
                className="rounded-md bg-gray-50 p-3"
                style={{ borderLeft: `4px solid ${ratingColor(comment.rating)}` }}
              >
                <div className="flex items-start justify-between">
                  <div>
                    <div className="font-semibold text-gray-900">{comment.authorName || "—"}</div>
                    {(comment.authorRole || comment.authorLocation) && (
                      <div className="text-xs text-gray-500 mt-0.5">
                        {[comment.authorRole, comment.authorLocation].filter(Boolean).join(" - ")}
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-2 whitespace-nowrap pl-2">
                    {comment.date && <span className="text-xs text-gray-400">{comment.date}</span>}
                    {/* A user can edit only their own feedback comment. */}
                    {onEdit && isOwn && (
                      <EditOutlined
                        className="text-gray-400 cursor-pointer hover:text-[#1677FF]"
                        onClick={() => onEdit(comment)}
                      />
                    )}
                  </div>
                </div>

                {comment.topics && comment.topics.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    {comment.topics.map((topic, index) => (
                      <Tag
                        key={`${comment.id}-topic-${index}`}
                        className="m-0 text-[13px] px-2.5 py-0.5"
                        style={TOPIC_TAG_STYLE}
                      >
                        {topic}
                      </Tag>
                    ))}
                  </div>
                )}

                {comment.comment && (
                  <Typography.Paragraph className="mt-2 mb-0 text-gray-700 whitespace-pre-wrap">
                    {comment.comment}
                  </Typography.Paragraph>
                )}

                {isResolved ? (
                  <div className="mt-2">
                    <div className="text-xs text-[#52C41A]">• Resolved</div>
                    {comment.resolvedBy && (
                      <div className="mt-0.5 text-xs text-gray-400">
                        Resolved by: {comment.resolvedBy}
                        {comment.resolvedTime ? ` - ${comment.resolvedTime}` : ""}
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="mt-2 text-xs text-[#FA8C16]">• Awaiting Resolution</div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {!loading && !hasOwnFeedback && (
        <Button
          type="link"
          className="mt-3 px-0"
          icon={<PlusOutlined />}
          onClick={() => onAddNew?.()}
        >
          Add New Feedback
        </Button>
      )}
    </Drawer>
  );
};

export default MessageFeedbackList;
